// src/components/automation/AutomationRulesTab.tsx
import React, { useState, useEffect } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { automationService, AUTOMATION_TEMPLATES } from '../../services/automation.service';
import { AutomationRule, AutomationTemplate } from '../../types/automation.types';
import { RulesList } from './RulesList';
import { TemplateGallery } from './TemplateGallery';
import { ExecutionHistory } from './ExecutionHistory';
import { CreateRuleModal } from './CreateRuleModal';
import {
  Plus,
  Zap,
  FileText,
  History,
  Settings2,
  AlertTriangle
} from 'lucide-react';

type SubTab = 'rules' | 'templates' | 'history';

export const AutomationRulesTab: React.FC = () => {
  const { userProfile } = useAuth();
  const [subTab, setSubTab] = useState<SubTab>('rules');
  const [rules, setRules] = useState<AutomationRule[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState<AutomationTemplate | null>(null);
  const [editingRule, setEditingRule] = useState<AutomationRule | null>(null);

  const isProviderAdmin = userProfile?.role === 'provider_admin';

  useEffect(() => {
    if (!isProviderAdmin) return;

    const unsubscribe = automationService.subscribeToRules((fetchedRules) => {
      setRules(fetchedRules);
      setLoading(false);
    });
    return () => unsubscribe();
  }, [isProviderAdmin]);

  // Only provider_admin can access
  if (!isProviderAdmin) {
    return (
      <div className="text-center py-12">
        <AlertTriangle className="h-12 w-12 text-yellow-500 mx-auto mb-4" />
        <h3 className="text-lg font-medium text-gray-900">Access Restricted</h3>
        <p className="text-gray-500 mt-2">
          Only administrators can manage automation rules.
        </p>
      </div>
    );
  }

  const handleCreateFromTemplate = (template: AutomationTemplate) => {
    setSelectedTemplate(template);
    setEditingRule(null);
    setShowCreateModal(true);
  };

  const handleCreateCustom = () => {
    setSelectedTemplate(null);
    setEditingRule(null);
    setShowCreateModal(true);
  };

  const handleEditRule = (rule: AutomationRule) => {
    setEditingRule(rule);
    setSelectedTemplate(null);
    setShowCreateModal(true);
  };

  const handleToggleRule = async (ruleId: string, enabled: boolean) => {
    if (!userProfile) return;
    await automationService.toggleRule(ruleId, enabled, userProfile.uid);
  };

  const handleDeleteRule = async (ruleId: string) => {
    if (window.confirm('Are you sure you want to delete this automation rule?')) {
      await automationService.deleteRule(ruleId);
    }
  };

  const enabledRulesCount = rules.filter(r => r.enabled).length;

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary-600" />
            Automation & Reminders
          </h2>
          <p className="text-sm text-gray-500 mt-1">
            Create rules to automate tasks and notifications based on events
          </p>
        </div>
        <button
          onClick={handleCreateCustom}
          className="flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors"
        >
          <Plus className="h-4 w-4" />
          Create Rule
        </button>
      </div>

      {/* Sub-tabs */}
      <div className="border-b border-gray-200">
        <nav className="flex space-x-8">
          <button
            onClick={() => setSubTab('rules')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
              subTab === 'rules'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <Settings2 className="h-4 w-4" />
            Active Rules ({enabledRulesCount})
          </button>
          <button
            onClick={() => setSubTab('templates')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
              subTab === 'templates'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <FileText className="h-4 w-4" />
            Templates ({AUTOMATION_TEMPLATES.length})
          </button>
          <button
            onClick={() => setSubTab('history')}
            className={`py-2 px-1 border-b-2 font-medium text-sm flex items-center gap-2 transition-colors ${
              subTab === 'history'
                ? 'border-primary-500 text-primary-600'
                : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
            }`}
          >
            <History className="h-4 w-4" />
            Execution History
          </button>
        </nav>
      </div>

      {/* Content */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-primary-600 border-t-transparent rounded-full mx-auto"></div>
          <p className="text-gray-500 mt-4">Loading...</p>
        </div>
      ) : (
        <>
          {subTab === 'rules' && (
            <RulesList
              rules={rules}
              onEdit={handleEditRule}
              onToggle={handleToggleRule}
              onDelete={handleDeleteRule}
            />
          )}
          {subTab === 'templates' && (
            <TemplateGallery
              templates={AUTOMATION_TEMPLATES}
              onSelect={handleCreateFromTemplate}
            />
          )}
          {subTab === 'history' && (
            <ExecutionHistory />
          )}
        </>
      )}

      {/* Create/Edit Modal */}
      {showCreateModal && (
        <CreateRuleModal
          isOpen={showCreateModal}
          onClose={() => {
            setShowCreateModal(false);
            setSelectedTemplate(null);
            setEditingRule(null);
          }}
          template={selectedTemplate}
          editingRule={editingRule}
        />
      )}
    </div>
  );
};

export default AutomationRulesTab;
